/**
 * Trainer/Admin-View des Recovery-Verlaufs eines Kunden.
 *
 * Liest `/training-sync/recovery/customer/{id}` (siehe
 * routes/training_sync_routes.py). Zeigt:
 *  - Durchschnittsscore über die gewählten Tage (Hero-Ring)
 *  - Trend-Kacheln (Schlaf / Energie / Stress)
 *  - Tagesliste mit Score-Ring + Metrik-Chips
 *  - 7/30 Tage-Toggle
 *
 * Schreiben ist hier bewusst NICHT erlaubt (Recovery ist eine
 * Selbst-Einschätzung des Kunden, der Trainer soll sie nur sehen).
 *
 * Redesign 06/2026: Dark-Theme → helles Admin-Design (User-Wunsch).
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Loader2, Moon, Battery, Brain, Dumbbell, Heart, RefreshCw, BedDouble } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-CH', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
  } catch {
    return iso;
  }
};

const scoreTheme = (s) => {
  if (s == null) return { text: 'text-gray-400', ring: '#d1d5db', bg: 'bg-gray-50', border: 'border-gray-200', label: '—' };
  if (s >= 70) return { text: 'text-emerald-600', ring: '#059669', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Erholt' };
  if (s >= 50) return { text: 'text-amber-600', ring: '#d97706', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Mittel' };
  return { text: 'text-rose-600', ring: '#e11d48', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Erschöpft' };
};

const avgSoreness = (sor) => {
  if (!sor || typeof sor !== 'object') return null;
  const vals = Object.values(sor).filter((v) => typeof v === 'number');
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

const quickScore = (e) => {
  // Mirror der Backend-`_quick_recovery_score`-Formel.
  const sh = e.sleep_hours ?? 0;
  const sq = e.sleep_quality ?? 3;
  const en = e.energy_level ?? 3;
  const st = e.stress_level ?? 3;
  const sor = avgSoreness(e.soreness);
  let s_score;
  if (sh >= 7 && sh <= 9) s_score = 30;
  else if (sh >= 6 && sh <= 10) s_score = 22;
  else s_score = Math.floor((Math.min(sh, 9) / 9) * 18);
  const sq_score = Math.round((sq / 5) * 20);
  const e_score = Math.round((en / 5) * 20);
  const st_score = Math.round(((6 - st) / 5) * 15);
  const sor_score = sor == null ? 10 : Math.max(0, Math.min(10, Math.round((1 - sor / 3) * 10)));
  return Math.max(0, Math.min(100, s_score + sq_score + e_score + st_score + sor_score + 5));
};

// Kleiner SVG-Ring für Scores
const ScoreRing = ({ score, size = 44 }) => {
  const t = scoreTheme(score);
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.min(100, Math.max(0, score));
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="4" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={t.ring} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${t.text}`}>
        {score ?? '—'}
      </span>
    </div>
  );
};

const MetricChip = ({ icon: Icon, label, accent }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-medium bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5 text-gray-600`}>
    <Icon size={11} className={accent} />
    {label}
  </span>
);

const CustomerRecoveryTab = ({ customerId }) => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ entries: [], avg_score: null });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/training-sync/recovery/customer/${customerId}?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data || { entries: [], avg_score: null });
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [customerId, days]);

  useEffect(() => {
    load();
  }, [load]);

  const entries = data.entries || [];
  const avgScore = data.avg_score;
  const avgTheme = scoreTheme(avgScore);

  // Trend pro Metrik (Δ erste vs. letzte Hälfte des Zeitraums)
  const trends = useMemo(() => {
    if (entries.length < 2) return null;
    const half = Math.floor(entries.length / 2);
    const recent = entries.slice(0, half).filter(Boolean);
    const older = entries.slice(half);
    const avg = (arr, key) => {
      const v = arr.map((e) => e[key]).filter((x) => typeof x === 'number');
      return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
    };
    return {
      sleep: [avg(older, 'sleep_hours'), avg(recent, 'sleep_hours')],
      energy: [avg(older, 'energy_level'), avg(recent, 'energy_level')],
      stress: [avg(older, 'stress_level'), avg(recent, 'stress_level')],
    };
  }, [entries]);

  return (
    <div className="space-y-4" data-testid="customer-recovery-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-rose-50 border border-rose-200 flex items-center justify-center">
              <Heart size={16} className="text-rose-500" />
            </span>
            Recovery-Verlauf
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Selbst-Einschätzung des Kunden · Schlaf, Energie, Stress, Muskelkater
          </p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setDays(7)}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition ${days === 7 ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="recovery-range-7"
          >
            7 Tage
          </button>
          <button
            type="button"
            onClick={() => setDays(30)}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition ${days === 30 ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="recovery-range-30"
          >
            30 Tage
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-2 py-1 text-gray-400 hover:text-teal-700 transition"
            data-testid="recovery-refresh"
            title="Aktualisieren"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400 text-sm bg-gray-50 rounded-xl border border-gray-200">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Lade Recovery-Daten…
        </div>
      ) : error ? (
        <div className="text-rose-600 text-sm py-8 text-center bg-rose-50 border border-rose-200 rounded-xl">
          Fehler: {error}
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 border border-dashed border-gray-300 rounded-xl" data-testid="recovery-empty">
          <BedDouble className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm font-medium text-gray-500">Noch keine Recovery-Einträge</p>
          <p className="text-xs text-gray-400 mt-1">in den letzten {days} Tagen</p>
        </div>
      ) : (
        <>
          {/* Übersicht: Ø Score + Trends */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div
              className={`rounded-xl border ${avgTheme.border} ${avgTheme.bg} px-4 py-3 flex items-center gap-3`}
              data-testid="recovery-avg-score"
            >
              <ScoreRing score={avgScore} size={52} />
              <div>
                <p className="text-[10px] uppercase font-bold tracking-wider text-gray-500">
                  Ø Score {days} T
                </p>
                <p className={`text-sm font-bold ${avgTheme.text}`}>{avgTheme.label}</p>
                <p className="text-[10px] text-gray-400">{entries.length} Einträge</p>
              </div>
            </div>
            {trends && (
              <>
                <TrendStat label="Ø Schlaf" unit="h" prev={trends.sleep[0]} now={trends.sleep[1]} icon={Moon} accent="text-blue-500" />
                <TrendStat label="Ø Energie" unit="/5" prev={trends.energy[0]} now={trends.energy[1]} icon={Battery} accent="text-emerald-500" />
                <TrendStat label="Ø Stress" unit="/5" prev={trends.stress[0]} now={trends.stress[1]} icon={Brain} accent="text-amber-500" lowerBetter />
              </>
            )}
          </div>

          {/* Tagesliste */}
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[480px] overflow-y-auto" data-testid="recovery-entry-list">
            {entries.map((e) => {
              const score = quickScore(e);
              const sor = avgSoreness(e.soreness);
              return (
                <div
                  key={e.id || e.date}
                  className="p-3 flex items-center gap-3 hover:bg-gray-50 transition"
                  data-testid={`recovery-entry-${e.date}`}
                >
                  <ScoreRing score={score} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-800">{fmtDate(e.date)}</p>
                      {sor != null && sor >= 2 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                          <Dumbbell size={10} />
                          Muskelkater
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {e.sleep_hours != null && <MetricChip icon={Moon} accent="text-blue-500" label={`${e.sleep_hours}h Schlaf`} />}
                      {e.energy_level != null && <MetricChip icon={Battery} accent="text-emerald-500" label={`Energie ${e.energy_level}/5`} />}
                      {e.stress_level != null && <MetricChip icon={Brain} accent="text-amber-500" label={`Stress ${e.stress_level}/5`} />}
                      {sor != null && <MetricChip icon={Dumbbell} accent="text-rose-500" label={`Kater ${sor.toFixed(1)}/3`} />}
                      {e.resting_hr != null && <MetricChip icon={Heart} accent="text-rose-500" label={`RHR ${Math.round(e.resting_hr)}`} />}
                    </div>
                    {e.notes && (
                      <p className="text-xs text-gray-500 italic mt-1 truncate">„{e.notes}"</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const TrendStat = ({ label, prev, now, unit, icon: Icon, accent, lowerBetter }) => {
  const hasBoth = typeof prev === 'number' && typeof now === 'number';
  const delta = hasBoth ? now - prev : null;
  const good = delta == null ? null : lowerBetter ? delta < 0 : delta > 0;
  const arrow = delta == null || Math.abs(delta) < 0.05 ? '·' : delta > 0 ? '↑' : '↓';
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <p className="text-[10px] uppercase font-bold tracking-wider text-gray-400 flex items-center gap-1">
        {Icon && <Icon size={12} className={accent} />}
        {label}
      </p>
      <p className="text-xl font-extrabold mt-1 text-gray-800">
        {now != null ? `${now.toFixed(1)}${unit}` : '—'}
      </p>
      {delta != null && (
        <p className={`text-[10px] font-bold mt-1 ${good ? 'text-emerald-600' : 'text-rose-500'}`}>
          {arrow} {delta > 0 ? '+' : ''}
          {delta.toFixed(1)} ggü. Vorperiode
        </p>
      )}
    </div>
  );
};

export default CustomerRecoveryTab;
